import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HOME } from "@/constants/testIds";
import { ProductPreview } from "@/components/sections/ProductPreview";

export function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Hairline grid background, masked into a soft ellipse */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-grid-dark mask-fade dark:opacity-100 opacity-0"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-grid-light mask-fade dark:opacity-0 opacity-100"
      />
      {/* Single, very subtle indigo halo — used sparingly per design rules */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-220px] h-[440px] w-[760px] -translate-x-1/2 rounded-full bg-primary/15 blur-3xl dark:bg-primary/20"
      />

      <div className="relative mx-auto w-full max-w-6xl px-5 pb-20 pt-20 sm:px-8 sm:pb-28 sm:pt-28">
        <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
          <Link
            to="/changelog"
            data-testid={HOME.heroBadge}
            className="group mb-7 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
          >
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span>Now in private beta — for Upwork & Fiverr pros</span>
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </Link>

          <h1
            data-testid={HOME.heroTitle}
            className="text-balance text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl"
          >
            Win more clients with{" "}
            <span className="text-primary">replies that sound like you.</span>
          </h1>

          <p
            data-testid={HOME.heroSubtitle}
            className="mt-6 max-w-xl text-balance text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            MACHA is the freelancer&apos;s copilot for Upwork, Fiverr and
            Freelancer. Draft proposals, polish replies, and never lose a
            client to a slow follow-up again.
          </p>

          <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="h-11 rounded-xl bg-primary px-5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
            >
              <Link to="/workspace" data-testid={HOME.heroPrimaryCta}>
                Start writing better proposals
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-11 rounded-xl border-border bg-transparent px-5 text-sm font-medium text-foreground hover:bg-secondary"
            >
              <Link to="/features" data-testid={HOME.heroSecondaryCta}>
                See how it works
              </Link>
            </Button>
          </div>

          <p className="mt-5 text-xs text-muted-foreground">
            Free to try · No credit card · Works with your existing clients
          </p>
        </div>

        <div data-testid={HOME.heroPreview} className="relative mt-16 sm:mt-20">
          <ProductPreview />
        </div>
      </div>
    </section>
  );
}
